import api from '../services/api';
import { UPLOAD_BASE } from './apiBase';

/**
 * A resumable video upload (messages handoff §4b — 2 GB / 15 min clips).
 *
 * The file goes to OUR API in chunks (tus-shaped: create → PATCH at an offset → finish) and the
 * API relays each chunk to Cloudflare Stream, so the browser never has to reach Cloudflare
 * (that failed on some networks) and nothing on the box ever holds more than a chunk. A chunk
 * that fails is retried from the offset the server really has; an upload that was interrupted
 * (a reload, a lost connection, RETRY on the tile) resumes where it left off because the upload
 * id is remembered per file in localStorage.
 *
 *   const { uploadId } = await uploadVideoResumable({ threadId, file, onProgress, signal });
 *
 * `onProgress(pct)` is whole-file progress. Throws a plain Error with `.isNetwork` when the
 * connection is what failed, so the outbox can word it.
 */
export const CHUNK_RETRIES = 4;
const STORE_PREFIX = 'pr.vidup.';

export function fingerprint(threadId, file) {
  return `${threadId}:${file.name || 'clip'}:${file.size}:${file.lastModified || 0}:${file.type || ''}`;
}

const remember = (fp, uploadId) => { try { localStorage.setItem(STORE_PREFIX + fp, uploadId); } catch { /* private mode */ } };
const recall = (fp) => { try { return localStorage.getItem(STORE_PREFIX + fp); } catch { return null; } };
export const forget = (threadId, file) => { try { localStorage.removeItem(STORE_PREFIX + fingerprint(threadId, file)); } catch { /* private mode */ } };

const isNetwork = (e) => !e?.response;
const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

async function begin(threadId, file) {
  const r = await api.post(`/messages/threads/${threadId}/video/uploads`, { sizeBytes: file.size, fileName: file.name || 'clip.mp4', contentType: file.type || 'video/mp4' });
  const d = r.data?.data;
  if (!d?.uploadId) throw Object.assign(new Error(r.data?.error?.message || 'Could not start the upload.'), { isNetwork: false });
  return d;
}

async function head(uploadId) {
  const r = await api.get(`/messages/video/uploads/${uploadId}`);
  return r.data?.data || null;
}

/** Where to continue a remembered upload from — or null when the server no longer has it. */
async function resumeOrBegin(threadId, file) {
  const fp = fingerprint(threadId, file);
  const known = recall(fp);
  if (known) {
    try {
      const d = await head(known);
      if (d && d.sizeBytes === file.size) return d;
    } catch (e) {
      if (isNetwork(e)) throw Object.assign(new Error('No connection.'), { isNetwork: true });
      // 404: the server swept it; start over.
    }
    forget(threadId, file);
  }
  const d = await begin(threadId, file);
  remember(fp, d.uploadId);
  return d;
}

/**
 * Send one chunk. Resolves to the new offset; on a 409 the server says where it really is and
 * the caller continues from there.
 */
async function patch(uploadId, offset, blob, onLoaded, signal) {
  try {
    const r = await api.patch(`/messages/video/uploads/${uploadId}`, blob, {
      baseURL: UPLOAD_BASE,
      headers: { 'Content-Type': 'application/offset+octet-stream', 'Upload-Offset': String(offset), 'Tus-Resumable': '1.0.0' },
      timeout: 20 * 60 * 1000,
      signal,
      onUploadProgress: (e) => onLoaded?.(e.loaded || 0),
    });
    const next = Number(r.headers?.['upload-offset'] ?? r.data?.data?.offset);
    return Number.isFinite(next) ? next : offset + blob.size;
  } catch (e) {
    if (e?.response?.status === 409) {
      const real = Number(e.response.headers?.['upload-offset'] ?? e.response.data?.data?.offset);
      if (Number.isFinite(real)) return real;
    }
    throw e;
  }
}

export const retryDelayMs = (failures) => Math.min(8000, 500 * 2 ** failures);

export default async function uploadVideoResumable({ threadId, file, onProgress = null, signal = null, retryDelay = retryDelayMs }) {
  let state;
  try { state = await resumeOrBegin(threadId, file); }
  catch (e) { throw Object.assign(new Error(e.message || 'Could not start the upload.'), { isNetwork: e.isNetwork ?? isNetwork(e) }); }
  const { uploadId } = state;
  // The server names the chunk (50 MiB: a multiple of 256 KiB, as Cloudflare wants).
  const chunk = state.chunkBytes || 50 * 1024 * 1024;
  let offset = state.offset || 0;
  const report = (loaded) => onProgress?.(Math.min(100, Math.round(((offset + loaded) * 100) / Math.max(1, file.size))));
  report(0);
  let failures = 0;
  while (offset < file.size) {
    if (signal?.aborted) throw Object.assign(new Error('Cancelled.'), { isNetwork: false, cancelled: true });
    const end = Math.min(file.size, offset + chunk);
    const blob = file.slice(offset, end);
    try {
      // A 409 answers with the server's real offset; either way we continue from what it says.
      offset = await patch(uploadId, offset, blob, report, signal);
      failures = 0;
      report(0);
    } catch (e) {
      if (signal?.aborted) throw Object.assign(new Error('Cancelled.'), { isNetwork: false, cancelled: true });
      if (e?.response && e.response.status !== 409 && e.response.status < 500) {
        // A refusal (gone, over the end, not yours): no point retrying.
        forget(threadId, file);
        throw Object.assign(new Error(e.response.data?.error?.message || 'Upload refused.'), { isNetwork: false });
      }
      failures += 1;
      if (failures > CHUNK_RETRIES) throw Object.assign(new Error('The connection kept dropping.'), { isNetwork: true });
      await sleep(retryDelay(failures));
      // Ask where we really are before sending again.
      try { const d = await head(uploadId); if (d) offset = d.offset; } catch { /* keep our offset */ }
    }
  }
  forget(threadId, file);
  return { uploadId };
}
